import { AJUDA_ESTOQUE } from "@/lib/ajuda/dialogos/estoque";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { parsePriceInput, formatPriceCents } from "@/components/services/service-meta";
import { MaterialCamposDeCompra, COMPRA_VAZIA, type DadosDeCompra } from "./MaterialCamposDeCompra";
import { MaterialCategoriaLab } from "./MaterialCategoriaLab";
import { MaterialQuimica } from "./MaterialQuimica";
import { PatientTagInput } from "@/components/patients/PatientTagInput";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

const NEW_MANUFACTURER = "__new__";

/** O material como ele volta de `myMaterialLines` — só o que este diálogo precisa pra editar. */
export type MaterialLineEditTarget = {
  id: string;
  name: string;
  materialCategory: { id: string; label: string };
  manufacturer: { id: string; name: string };
  subcategoryId: string | null;
  supplierPartnerId: string | null;
  unit: string | null;
  purchasePriceCents: number | null;
  defaultCorrectionFactor: number | null;
  minimumStock: number | null;
  indirectConsumption: boolean;
  tags: string[];
};

const paraNumero = (v: string) => (v.trim() ? Number(v.replace(",", ".")) : null);

/**
 * Cadastro de material — criar e editar no mesmo diálogo.
 *
 * Editando, fabricante e categoria ficam travados: trocá-los muda a identidade do material, e a
 * ficha técnica montada em cima dele passaria a significar outra coisa sem ninguém perceber. O
 * servidor recusa esses campos no update pelo mesmo motivo.
 */
export function MaterialLineDialog({
  open,
  onClose,
  editTarget,
  categoriaInicial,
  onCriado,
}: {
  open: boolean;
  onClose: () => void;
  editTarget?: MaterialLineEditTarget | null;
  /** Quem clicou em "Adicionar" dentro de um container já disse a categoria — o campo vem pronto. */
  categoriaInicial?: string | null;
  /** Avisa quem abriu este diálogo de fora qual material nasceu. A entrada de estoque usa pra já
   *  pôr o material na nota, em vez de mandar procurar na lista o que a pessoa acabou de criar. */
  onCriado?: (material: { id: string; name: string; purchasePriceCents: number | null }) => void;
}) {
  const utils = trpc.useUtils();
  const manufacturersQuery = trpc.prosthesis.catalog.manufacturers.useQuery();
  const editando = !!editTarget;

  const [materialCategoryId, setMaterialCategoryId] = useState("");
  const [manufacturerId, setManufacturerId] = useState("");
  const [newManufacturerName, setNewManufacturerName] = useState("");
  const [name, setName] = useState("");
  const [compra, setCompra] = useState<DadosDeCompra>(COMPRA_VAZIA);
  const [tags, setTags] = useState<string[]>([]);
  const tagsQuery = trpc.prosthesis.catalog.listMaterialTags.useQuery(undefined, { enabled: open });

  // `useCallback` porque o efeito de abertura depende dela e ela depende de `categoriaInicial`:
  // sem isso, abrir o diálogo por outro container não trocaria a categoria pré-selecionada.
  const reset = useCallback(() => {
    setMaterialCategoryId("");
    setManufacturerId("");
    setNewManufacturerName("");
    setName("");
    setCompra({ ...COMPRA_VAZIA, subcategoryId: categoriaInicial ?? "" });
    setTags([]);
  }, [categoriaInicial]);

  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setMaterialCategoryId(editTarget.materialCategory.id);
      setManufacturerId(editTarget.manufacturer.id);
      setName(editTarget.name);
      setCompra({
        subcategoryId: editTarget.subcategoryId ?? "",
        supplierPartnerId: editTarget.supplierPartnerId ?? "",
        unit: editTarget.unit ?? "",
        preco: editTarget.purchasePriceCents != null ? formatPriceCents(editTarget.purchasePriceCents) : "",
        fator: editTarget.defaultCorrectionFactor != null ? String(editTarget.defaultCorrectionFactor).replace(".", ",") : "",
        minimo: editTarget.minimumStock != null ? String(editTarget.minimumStock).replace(".", ",") : "",
        indirectConsumption: editTarget.indirectConsumption,
      });
      setTags(editTarget.tags ?? []);
    } else {
      reset();
    }
  }, [open, editTarget, categoriaInicial, reset]);

  const aoSalvar = (msg: string) => () => {
    toast.success(msg);
    utils.prosthesis.catalog.myMaterialLines.invalidate();
    utils.prosthesis.catalog.materialLines.invalidate();
    utils.prosthesis.catalog.manufacturers.invalidate();
    // A sugestão de tag é derivada do que existe: sem isto, uma tag nova só apareceria depois de
    // um F5 (ver listMaterialTags).
    utils.prosthesis.catalog.listMaterialTags.invalidate();
    reset();
    onClose();
  };
  const aoFalhar = (e: { message: string }) => toast.error(e.message || "Erro ao salvar insumo.");

  const create = trpc.prosthesis.catalog.createCustomMaterialLine.useMutation({
    onSuccess: (criado: { id: string; name: string; purchasePriceCents: number | null }) => {
      onCriado?.({ id: criado.id, name: criado.name, purchasePriceCents: criado.purchasePriceCents });
      aoSalvar("Insumo adicionado.")();
    },
    onError: aoFalhar,
  });
  const update = trpc.prosthesis.catalog.updateCustomMaterialLine.useMutation({
    onSuccess: aoSalvar("Insumo atualizado."),
    onError: aoFalhar,
  });

  const isCreatingManufacturer = manufacturerId === NEW_MANUFACTURER;
  const canSubmit =
    name.trim().length >= 2 &&
    (editando ||
      (!!materialCategoryId &&
        (isCreatingManufacturer ? newManufacturerName.trim().length >= 2 : !!manufacturerId)));

  function submit() {
    const dados = {
      subcategoryId: compra.subcategoryId || null,
      supplierPartnerId: compra.supplierPartnerId || null,
      unit: compra.unit.trim() || null,
      purchasePriceCents: compra.preco.trim() ? parsePriceInput(compra.preco) : null,
      defaultCorrectionFactor: paraNumero(compra.fator),
      minimumStock: paraNumero(compra.minimo),
      indirectConsumption: compra.indirectConsumption,
      tags,
    };
    if (editTarget) {
      update.mutate({ id: editTarget.id, name: name.trim(), ...dados });
    } else {
      create.mutate({
        materialCategoryId,
        name: name.trim(),
        ...(isCreatingManufacturer ? { newManufacturerName: newManufacturerName.trim() } : { manufacturerId }),
        ...dados,
      });
    }
  }

  const salvando = create.isPending || update.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="flex max-h-[85dvh] max-w-md flex-col gap-3 overflow-y-auto dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_ESTOQUE.insumo}>{editando ? "Editar insumo" : "Novo insumo"}</DialogTitle>
        </DialogHeader>

        {/* Categoria (Brocas, Resinas) antes da subcategoria (a química): é o agrupamento mais
            largo, e quem cadastra pensa nele primeiro. */}
        <MaterialCategoriaLab
          valor={compra.subcategoryId}
          onChange={(subcategoryId) => setCompra((v) => ({ ...v, subcategoryId }))}
        />

        <MaterialQuimica
          valor={materialCategoryId}
          onChange={setMaterialCategoryId}
          desabilitado={editando}
        />

        <div>
          <p className={labelCls}>Fabricante</p>
          <select
            value={manufacturerId}
            onChange={(e) => setManufacturerId(e.target.value)}
            disabled={editando}
            className={`${inputCls} disabled:opacity-60`}
          >
            <option value="">Selecione...</option>
            {(manufacturersQuery.data ?? []).map((m: { id: string; name: string }) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
            <option value={NEW_MANUFACTURER}>+ Novo fabricante</option>
          </select>
          {isCreatingManufacturer && !editando && (
            <input
              value={newManufacturerName}
              onChange={(e) => setNewManufacturerName(e.target.value)}
              placeholder="Nome do novo fabricante"
              className={`${inputCls} mt-2`}
              autoFocus
            />
          )}
          {editando && (
            <p className="mt-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
              Subcategoria e fabricante não mudam: ficha técnica já montada em cima deste material
              passaria a significar outra coisa. Para trocar, crie outro material.
            </p>
          )}
        </div>

        <div>
          <p className={labelCls}>Linha</p>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Bloco C14, IPS e.max CAD"
            className={inputCls}
          />
        </div>

        <div>
          <p className={labelCls}>Tags</p>
          {/* O mesmo campo das tags de paciente — ele já é agnóstico de domínio, e reaproveitar
              mantém o gesto (digitar, Enter, cor por nome) igual nas duas telas. */}
          <PatientTagInput
            value={tags}
            onChange={setTags}
            suggestions={tagsQuery.data ?? []}
            placeholder="Ex: importado, em falta"
          />
        </div>

        <MaterialCamposDeCompra valor={compra} onChange={setCompra} />

        <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={salvando || !canSubmit}
            onClick={submit}
            className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            {editando ? "Salvar" : "Adicionar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
